import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Link2, Lock, Play, CheckCheck, FileText, Trash2, Plus, PenLine, Ban, History, UserRound, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PlateChip } from '../components/PlateChip';
import { StatusPill } from '../components/StatusPill';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PhotoUploader } from '../components/PhotoUploader';
import { useUpgrade, planLabel } from '../components/UpgradeModal';
import { api, errMsg, isUpgradeErr, copyToClipboard } from '../lib/api';
import { peso, manilaDateTime, FUEL_LABELS, METHOD_LABELS } from '../lib/format';
import { useAuth } from '../lib/AuthContext';

const EDITABLE = ['received', 'estimate_sent'];

function LineItemsEditor({ detail, onSaved }) {
  const [rows, setRows] = useState(
    detail.line_items.map((i) => ({ type: i.type, description: i.description, qty: String(i.qty), price: (i.unit_price_centavos / 100).toFixed(2) }))
  );
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const editable = EDITABLE.includes(detail.job.status);

  const update = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...next[i], [k]: v };
    setRows(next);
    setDirty(true);
  };
  const addRow = (type) => { setRows([...rows, { type, description: '', qty: '1', price: '' }]); setDirty(true); };
  const removeRow = (i) => { setRows(rows.filter((_, idx) => idx !== i)); setDirty(true); };

  const total = rows.reduce((sum, r) => {
    const q = parseFloat(r.qty) || 0;
    const p = Math.round((parseFloat(r.price) || 0) * 100);
    return sum + Math.round(q * p);
  }, 0);

  const save = async () => {
    for (const r of rows) {
      if (!r.description.trim()) { toast.error('Every line item needs a description.'); return; }
      if (!(parseFloat(r.qty) > 0)) { toast.error('Quantity must be greater than zero.'); return; }
      if (parseFloat(r.price) < 0 || r.price === '') { toast.error('Unit price can\u2019t be empty or negative.'); return; }
    }
    setBusy(true);
    try {
      await api.put(`/jobs/${detail.job.id}/line-items`, {
        items: rows.map((r) => ({
          type: r.type, description: r.description.trim(),
          qty: parseFloat(r.qty), unit_price_centavos: Math.round(parseFloat(r.price) * 100),
        })),
      });
      toast.success('Estimate updated');
      setDirty(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold font-display">Estimate</h2>
          {!editable && <span className="text-xs text-[hsl(var(--muted-foreground))]">Locked — use “Revise estimate” to edit</span>}
        </div>
        {rows.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No line items yet. Add labor or parts below.</p>}
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} data-testid="line-item-row" className="rounded-xl border border-[hsl(var(--border))] p-2.5 space-y-2">
              <div className="flex gap-2">
                <Select value={r.type} onValueChange={(v) => update(i, 'type', v)} disabled={!editable}>
                  <SelectTrigger data-testid="line-item-type-select" className="h-10 rounded-lg w-[104px] shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labor">Labor</SelectItem>
                    <SelectItem value="part">Part</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="line-item-description-input" disabled={!editable} placeholder="Description" value={r.description} onChange={(e) => update(i, 'description', e.target.value)} className="h-10 rounded-lg flex-1" />
                {editable && (
                  <Button data-testid="line-item-remove-button" variant="ghost" size="icon" aria-label="Remove line item" className="h-10 w-10 rounded-lg text-[hsl(var(--destructive))]" onClick={() => removeRow(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <Input data-testid="line-item-qty-input" disabled={!editable} type="number" step="0.5" min="0.5" placeholder="Qty" value={r.qty} onChange={(e) => update(i, 'qty', e.target.value)} className="h-10 rounded-lg w-20" />
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">₱</span>
                  <Input data-testid="line-item-price-input" disabled={!editable} type="number" step="0.01" min="0" placeholder="Unit price" value={r.price} onChange={(e) => update(i, 'price', e.target.value)} className="h-10 rounded-lg pl-7" />
                </div>
                <span className="text-sm font-medium tabular-nums w-24 text-right">
                  {peso(Math.round((parseFloat(r.qty) || 0) * Math.round((parseFloat(r.price) || 0) * 100)))}
                </span>
              </div>
            </div>
          ))}
        </div>
        {editable && (
          <div className="flex gap-2">
            <Button data-testid="job-detail-add-labor-button" variant="outline" className="h-10 rounded-xl flex-1" onClick={() => addRow('labor')}>
              <Plus className="h-4 w-4 mr-1" /> Labor
            </Button>
            <Button data-testid="job-detail-add-part-button" variant="outline" className="h-10 rounded-xl flex-1" onClick={() => addRow('part')}>
              <Plus className="h-4 w-4 mr-1" /> Part
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[hsl(var(--border))] pt-3">
          <span className="text-sm font-medium">Running total</span>
          <span data-testid="job-detail-total" className="text-lg font-semibold tabular-nums font-display">{peso(total)}</span>
        </div>
        {editable && dirty && (
          <Button data-testid="job-detail-save-items-button" disabled={busy} onClick={save} className="w-full h-11 rounded-xl">
            {busy ? 'Saving…' : 'Save estimate'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const { openUpgrade } = useUpgrade();
  const features = me?.shop?.features || {};
  const role = me?.user?.role;
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [verbalOpen, setVerbalOpen] = useState(false);
  const [verbalName, setVerbalName] = useState('');
  const [confirm, setConfirm] = useState(null); // 'void' | 'revise'

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      if (data.technician_view) { navigate('/tech'); return; }
      setDetail(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(errMsg(e));
    }
  }, [jobId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (features.technician && role !== 'technician') {
      api.get('/shop/technicians').then(({ data }) => setTechs(data.technicians)).catch(() => {});
    }
  }, [features.technician, role]);

  const handleErr = (e) => {
    if (isUpgradeErr(e)) openUpgrade(planLabel(e.response.data.detail.required_plan));
    else toast.error(errMsg(e));
  };

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!features.estimate_links) { openUpgrade('Pro'); return; }
    act(async () => {
      const { data } = await api.post(`/jobs/${jobId}/estimate-link`);
      const ok = await copyToClipboard(data.url);
      toast.success(ok ? data.message : `Link ready: ${data.url}`);
    });
  };

  const copyTrackingLink = () => {
    if (!features.tracking_links) { openUpgrade('Pro'); return; }
    act(async () => {
      const { data } = await api.post(`/jobs/${jobId}/tracking-link`);
      const ok = await copyToClipboard(data.url);
      toast.success(ok ? data.message : `Link ready: ${data.url}`);
    });
  };

  const verbalApprove = () => act(async () => {
    const { data } = await api.post(`/jobs/${jobId}/verbal-approve`, { approver_name: verbalName });
    toast.success(data.message);
    setVerbalOpen(false);
    setVerbalName('');
  });

  const memoJob = useMemo(() => detail?.job, [detail]);

  if (notFound) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="font-medium">Job not found.</p>
        <Button variant="outline" className="rounded-xl" onClick={() => navigate('/board')}>Back to board</Button>
      </div>
    );
  }
  if (!detail) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>;
  }

  const { job, vehicle, customer, approvals, payments, estimate_links, billing_statement, assigned_technician, events, photos } = detail;
  const status = job.status;
  const isOwner = role === 'owner';
  const conditionPhotos = photos.filter((p) => p.kind === 'condition');
  const workPhotos = photos.filter((p) => p.kind === 'work');
  const activeLink = estimate_links.find((l) => l.status === 'active');

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <PlateChip plate={vehicle?.plate} size="lg" />
              <span data-testid="job-detail-jo-number" className="font-plate text-sm text-[hsl(var(--muted-foreground))]">{job.jo_number}</span>
            </div>
            <StatusPill status={status} />
          </div>
          <div className="text-base font-medium">{vehicle?.year} {vehicle?.make} {vehicle?.model}{vehicle?.color ? ` · ${vehicle.color}` : ''}</div>
          <div className="flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))] flex-wrap">
            <span className="flex items-center gap-1"><UserRound className="h-3.5 w-3.5" /> {customer?.name}</span>
            <a href={`tel:${customer?.mobile}`} className="flex items-center gap-1 text-[hsl(var(--primary))]"><Phone className="h-3.5 w-3.5" /> {customer?.mobile}</a>
          </div>
          <div className="rounded-xl bg-[hsl(var(--muted))] p-3 text-sm">“{job.complaint}”</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] flex gap-3 flex-wrap">
            <span>Received {manilaDateTime(job.received_at)}</span>
            {job.odometer_in != null && <span>· {Number(job.odometer_in).toLocaleString()} km</span>}
            {job.fuel_level && <span>· Fuel {FUEL_LABELS[job.fuel_level]}</span>}
          </div>
          {job.condition_notes && <p className="text-xs text-[hsl(var(--muted-foreground))]">Condition: {job.condition_notes}</p>}
        </CardContent>
      </Card>

      {/* Status actions */}
      {status !== 'voided' && status !== 'closed' && (
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-2.5">
            <h2 className="text-base font-semibold font-display">Next step</h2>
            {(status === 'received' || status === 'estimate_sent') && (
              <div className="space-y-2">
                <Button data-testid="job-detail-copy-estimate-link-button" disabled={busy} onClick={copyLink} className="w-full h-12 rounded-xl">
                  {features.estimate_links ? <Link2 className="h-4 w-4 mr-1.5" /> : <Lock className="h-4 w-4 mr-1.5" />}
                  Copy estimate link
                </Button>
                <Button data-testid="job-detail-verbal-approve-button" variant="outline" disabled={busy} onClick={() => setVerbalOpen(true)} className="w-full h-12 rounded-xl">
                  <CheckCheck className="h-4 w-4 mr-1.5" /> Customer approved verbally
                </Button>
                {status === 'estimate_sent' && activeLink && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Estimate link active — expires {manilaDateTime(activeLink.expires_at)}. Tap “Copy estimate link” to copy it again.</p>
                )}
              </div>
            )}
            {(status === 'approved' || status === 'in_progress' || status === 'ready') && (
              <Button data-testid="job-detail-copy-tracking-link-button" variant="outline" disabled={busy} onClick={copyTrackingLink} className="w-full h-12 rounded-xl">
                {features.tracking_links ? <Link2 className="h-4 w-4 mr-1.5" /> : <Lock className="h-4 w-4 mr-1.5" />}
                Copy tracking link
              </Button>
            )}
            {status === 'approved' && (
              <div className="space-y-2">
                {features.technician && (
                  <div className="flex items-center gap-2">
                    <Select value={job.assigned_technician_id || 'none'} onValueChange={(v) => act(async () => {
                      const { data } = await api.patch(`/jobs/${jobId}/assign`, { technician_id: v === 'none' ? null : v });
                      toast.success(data.message);
                    })}>
                      <SelectTrigger data-testid="job-detail-assign-tech-select" className="h-11 rounded-xl flex-1">
                        <SelectValue placeholder="Assign technician" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No technician</SelectItem>
                        {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button data-testid="job-detail-start-work-button" disabled={busy} onClick={() => act(async () => {
                  const { data } = await api.post(`/jobs/${jobId}/start`);
                  toast.success(data.message);
                })} className="w-full h-12 rounded-xl">
                  <Play className="h-4 w-4 mr-1.5" /> Start work
                </Button>
              </div>
            )}
            {status === 'in_progress' && (
              <Button data-testid="job-detail-mark-done-button" disabled={busy} onClick={() => act(async () => {
                const { data } = await api.post(`/jobs/${jobId}/done`);
                toast.success(data.message);
              })} className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-600/90">
                <CheckCheck className="h-4 w-4 mr-1.5" /> Mark work done
              </Button>
            )}
            {status === 'ready' && (
              <Button data-testid="job-detail-billing-button" onClick={() => navigate(`/billing/${jobId}`)} className="w-full h-12 rounded-xl">
                <FileText className="h-4 w-4 mr-1.5" /> {billing_statement ? 'Open billing statement' : 'Generate billing statement'}
              </Button>
            )}
            <div className="flex gap-2 pt-1">
              {(status === 'estimate_sent' || status === 'approved') && (
                <Button data-testid="job-detail-revise-button" variant="outline" disabled={busy} onClick={() => setConfirm('revise')} className="flex-1 h-10 rounded-xl text-sm">
                  <PenLine className="h-4 w-4 mr-1.5" /> Revise estimate
                </Button>
              )}
              {isOwner && (
                <Button data-testid="job-detail-void-button" variant="outline" disabled={busy} onClick={() => setConfirm('void')} className="flex-1 h-10 rounded-xl text-sm text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                  <Ban className="h-4 w-4 mr-1.5" /> Void job
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {status === 'closed' && (
        <Button data-testid="job-detail-billing-button" onClick={() => navigate(`/billing/${jobId}`)} variant="outline" className="w-full h-12 rounded-xl">
          <FileText className="h-4 w-4 mr-1.5" /> View billing statement
        </Button>
      )}

      {/* Line items */}
      <LineItemsEditor key={`${job.updated_at}-${detail.line_items.length}`} detail={detail} onSaved={load} />

      {/* Approval record */}
      {approvals.length > 0 && (
        <Card className="rounded-2xl border-emerald-100 bg-emerald-50/50 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-4 pb-4">
            <p data-testid="job-detail-approval-record" className="text-sm">
              <CheckCheck className="h-4 w-4 inline mr-1 text-emerald-600" />
              Approved by <span className="font-medium">{approvals[0].approver_name}</span> on {manilaDateTime(approvals[0].approved_at)}
              {' '}({approvals[0].source === 'verbal' ? 'verbal' : 'via link'}) — {peso(approvals[0].approved_total_centavos)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Payments */}
      {payments.length > 0 && (
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-2">
            <h2 className="text-base font-semibold font-display">Payments</h2>
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm border-b border-[hsl(var(--border))] last:border-0 pb-2 last:pb-0">
                <div>
                  <span className="font-medium">{METHOD_LABELS[p.method]}</span>
                  {p.reference && <span className="text-[hsl(var(--muted-foreground))]"> · {p.reference}</span>}
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">OR {p.or_number} · {manilaDateTime(p.recorded_at)}</div>
                </div>
                <span className="font-semibold tabular-nums">{peso(p.amount_centavos)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-4">
          <div>
            <h2 className="text-base font-semibold font-display mb-2">Condition photos</h2>
            <PhotoUploader jobId={jobId} kind="condition" photos={conditionPhotos} onChanged={load} canDelete max={8} />
          </div>
          {features.work_photos && (
            <div>
              <h2 className="text-base font-semibold font-display mb-2">Work photos</h2>
              {workPhotos.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No work photos yet — the technician can add them from their job view.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {workPhotos.map((p) => (
                    <img key={p.id} src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="work" className="aspect-square rounded-xl object-cover border border-[hsl(var(--border))]" loading="lazy" />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event history (Pro+) */}
      {features.event_history && events.length > 0 && (
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5">
            <h2 className="text-base font-semibold font-display mb-3 flex items-center gap-1.5"><History className="h-4 w-4" /> History</h2>
            <div className="space-y-2.5">
              {events.map((ev) => (
                <div key={ev.id} data-testid="job-event-row" className="text-xs flex gap-2">
                  <span className="text-[hsl(var(--muted-foreground))] shrink-0 w-32">{manilaDateTime(ev.created_at)}</span>
                  <span><span className="font-medium">{ev.user_name}</span> — {ev.event.replaceAll('_', ' ')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verbal approval dialog */}
      <Dialog open={verbalOpen} onOpenChange={setVerbalOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Customer approved verbally</DialogTitle>
            <DialogDescription>Type the customer's full name to record the approval. It is saved with date and time.</DialogDescription>
          </DialogHeader>
          <Input data-testid="verbal-approve-name-input" placeholder="Customer's full name" value={verbalName} onChange={(e) => setVerbalName(e.target.value)} className="h-11 rounded-xl" />
          <DialogFooter>
            <Button data-testid="verbal-approve-submit-button" disabled={busy || verbalName.trim().length < 2} onClick={verbalApprove} className="w-full h-11 rounded-xl">
              Record approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirm === 'revise'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Revise estimate?"
        consequence="This moves the job back to Received and the old estimate link stops working. The customer will need to approve the new estimate."
        confirmLabel="Revise estimate"
        busy={busy}
        onConfirm={() => act(async () => {
          const { data } = await api.post(`/jobs/${jobId}/revise`);
          toast.success(data.message);
          setConfirm(null);
        })}
      />
      <ConfirmDialog
        open={confirm === 'void'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Void ${memoJob?.jo_number}?`}
        consequence="The job is removed from the board and any estimate link stops working. This cannot be undone, but the record is kept."
        confirmLabel="Void job"
        destructive
        busy={busy}
        onConfirm={() => act(async () => {
          const { data } = await api.post(`/jobs/${jobId}/void`);
          toast.success(data.message);
          setConfirm(null);
          navigate('/board');
        })}
      />
    </div>
  );
}
